import { useContext, useEffect, useState } from 'react';

import { TabContext, TabList } from '@mui/lab';
import { Skeleton, Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useFlag } from '@unleash/proxy-client-react';
import { isAfter } from 'date-fns';
import { useIntl } from 'react-intl';
import { FeatureToggle } from 'utils/unleash/toggles';

import { SpecialistContext } from 'context';
import { useCareMatrix } from 'hooks/useCareMatrix';
import { EndUserState, getCareMatrixModuleUserData } from 'services/user';

import Session from './components/Session';
import { TABS } from './const';
import { Header } from './Header';
import { Activities } from './tabs/activities';
import { History } from './tabs/history';
import { Record } from './tabs/record';
import { Summary } from './tabs/summary';

import * as S from './styles';

export function CareMatrixModule() {
  const [activeTab, setActiveTab] = useState<TABS>(TABS.SUMMARY);
  const { specialistInfo } = useContext(SpecialistContext);

  const { patientCognitoId, sessionInfo } = useCareMatrix();
  const intl = useIntl();

  const isRecordTabEnabled = useFlag(FeatureToggle.RecordTab);

  const {
    isLoading,
    data: user,
    isError: isUserDataError,
  } = useQuery<EndUserState>({
    queryKey: ['getCareMatrixModuleUserData', patientCognitoId],
    queryFn: () => getCareMatrixModuleUserData(patientCognitoId),
  });

  function handleChangeTab(_: React.SyntheticEvent, newValue: TABS) {
    setActiveTab(newValue);
  }

  useEffect(() => {
    setActiveTab(TABS.SUMMARY);

    const wrapper = document.getElementById('careMatrixModule');

    if (wrapper) {
      wrapper.scrollTop = 0;
    }
  }, [patientCognitoId]);

  return (
    <S.Wrapper id="careMatrixModule">
      {isLoading && (
        <Box display="flex" flexDirection="column" gap={0.5} py={2} px={4}>
          <Skeleton width={176} height={32} variant="rounded" />

          <Box display="flex" alignItems="center" gap={0.5}>
            <Skeleton width={37} height={24} variant="rounded" />
            <Skeleton width={154} height={24} variant="rounded" />
          </Box>

          <Skeleton width="100%" height={42} variant="rounded" />
        </Box>
      )}

      <TabContext value={activeTab}>
        <S.Header>
          {user && <Header user={user} isError={isUserDataError} />}
          <Box mt={1}>
            <TabList onChange={handleChangeTab} centered variant="fullWidth">
              <S.Tab
                label={intl.formatMessage({
                  id: 'CAREMATRIXCPTC.TABMENU.SUMMARY',
                })}
                value={TABS.SUMMARY}
              />
              {specialistInfo?.geoCode && specialistInfo.geoCode === 'BR' && (
                <S.Tab
                  label={intl.formatMessage({
                    id: 'CAREMATRIXCPTC.TABMENU.ACTIVITIES',
                  })}
                  value={TABS.ACTIVITIES}
                />
              )}
              <S.Tab
                label={intl.formatMessage({
                  id: 'CAREMATRIXCPTC.TABMENU.HISTORY',
                })}
                value={TABS.HISTORY}
              />
              {isRecordTabEnabled && (
                <S.Tab
                  label={intl.formatMessage({
                    id: 'CAREMATRIXCPTC.TABMENU.RECORD',
                  })}
                  value={TABS.RECORD}
                  mustUpdate={user?.patient_record.must_update || false}
                />
              )}
            </TabList>
          </Box>
        </S.Header>

        <S.TabPanel value={TABS.SUMMARY}>
          <Summary />
        </S.TabPanel>

        <S.TabPanel value={TABS.ACTIVITIES}>
          <Activities />
        </S.TabPanel>

        <S.TabPanel value={TABS.HISTORY}>
          <History />
        </S.TabPanel>

        <S.TabPanel value={TABS.RECORD}>
          <Record mustUpdate={user?.patient_record.must_update || false} />
        </S.TabPanel>

        {sessionInfo && user && isAfter(sessionInfo.session.end, Date.now()) && (
          <S.Footer>
            <Session
              session={sessionInfo.session}
              deleteBooking={sessionInfo.deleteBooking}
              enduserFullName={user.fullName}
              enduserID={user.userID}
            />
          </S.Footer>
        )}
      </TabContext>
    </S.Wrapper>
  );
}
